import Link from 'next/link';
import { Children } from 'react';
import type { ReactNode } from 'react';
import type { PortableTextComponents } from '@portabletext/react';

import { BodyImage } from './blocks/BodyImage';
import type { SanityBodyImage } from './blocks/BodyImage';
import { CtaBannerBlock } from './blocks/CtaBannerBlock';
import { EmbedBlock } from './blocks/EmbedBlock';
import { FixtureTickerBlock } from './blocks/FixtureTickerBlock';
import { HeadToHeadBlock } from './blocks/HeadToHeadBlock';
import { PlayerFixturesBlock } from './blocks/PlayerFixturesBlock';
import { PlayerListBlock } from './blocks/PlayerListBlock';
import { PlayerProfileBlock } from './blocks/PlayerProfileBlock';
import { StatCalloutBlock } from './blocks/StatCalloutBlock';
import { ARTICLE_TOKENS, SCROLL_MARGIN_CLASS } from './design-tokens';
import { headingIdFor } from '@lib/article-blocks/headings';
import { toPlayerByIdPath } from '@lib/page-utils/player-profile/slugs';
import type { ArticleHeading } from '@lib/article-blocks/headings';
import { EMPTY_ARTICLE_BLOCK_DATA } from '@lib/article-blocks/hydrate';
import type {
  ArticleBlockData,
  SanityCtaBannerBlock,
  SanityEmbedBlock,
  SanityFixtureTickerBlock,
  SanityHeadToHeadBlock,
  SanityPlayerFixturesBlock,
  SanityPlayerLinkMark,
  SanityPlayerListBlock,
  SanityPlayerProfileBlock,
  SanityStatCalloutBlock
} from '@lib/article-blocks/types';

// The legacy WordPress stylesheet (`.article p a` in scss/_article.scss) still
// reaches these marks: it recolours them, draws a border-bottom underline, and
// its `text-decoration: none` shorthand outranks the underline utilities, which
// left links in paragraphs with no underline at all. The important modifiers
// let the marks own their colour and draw one visible text underline instead.
// Colours mirror ARTICLE_TOKENS.ink and ARTICLE_TOKENS.accentText; Tailwind
// only generates classes it can read literally, so they cannot be composed.
const INLINE_LINK_CLASS_NAME =
  '!border-b-0 !text-[#14171c] !underline !decoration-2 !decoration-[#1f7fc4] underline-offset-[3px] transition-colors hover:!text-[#1f7fc4]';

const LEADING_SPACE = /^\s+/;
const TRAILING_SPACE = /\s+$/;

/**
 * Renders a link mark with any space at either end of its text moved outside
 * the link. Double-clicking a word on Windows selects the space next to it, so
 * links made that way would otherwise underline the gap.
 */
const withEdgeSpacesOutside = (
  children: ReactNode,
  renderLink: (linkText: ReactNode) => ReactNode
) => {
  const nodes = Children.toArray(children);
  const lastIndex = nodes.length - 1;
  const first = nodes[0];
  const last = nodes[lastIndex];
  const leading =
    typeof first === 'string' ? first.match(LEADING_SPACE)?.[0] ?? '' : '';
  const trailing =
    typeof last === 'string' ? last.match(TRAILING_SPACE)?.[0] ?? '' : '';
  const isOnlySpace =
    nodes.length === 1 && typeof first === 'string' && !first.trim();
  if ((!leading && !trailing) || isOnlySpace) return renderLink(children);

  const linkText = nodes.map((node, index) => {
    if (typeof node !== 'string') return node;
    const start = index === 0 ? leading.length : 0;
    const end =
      index === lastIndex ? node.length - trailing.length : node.length;
    return node.slice(start, end);
  });
  return (
    <>
      {leading}
      {renderLink(linkText)}
      {trailing}
    </>
  );
};

/**
 * Custom object blocks (player cards, fixture tickers, FFH-2712) register here
 * so every article route renders the same set. Block data is closed over
 * rather than read from context so the block components stay hook-free.
 */
export const buildArticlePortableTextComponents = (
  blockData: ArticleBlockData = EMPTY_ARTICLE_BLOCK_DATA,
  headings: ArticleHeading[] = []
): PortableTextComponents => ({
  block: {
    normal: ({ children }) => (
      <p
        className={`mb-5 font-body text-lg leading-[1.65] ${ARTICLE_TOKENS.body}`}
      >
        {children}
      </p>
    ),
    // The heavy rule above each H2 is what gives long list articles their
    // section rhythm; the id is what the table of contents links to.
    h2: ({ children, value }) => (
      <h2
        id={headingIdFor(
          headings,
          (value.children ?? [])
            .map((child) => String(child.text ?? ''))
            .join('')
            .trim()
        )}
        className={`mt-[52px] mb-2 border-t-2 border-[#14171c] pt-5 font-heading text-[27px] font-bold leading-[1.2] tracking-[-0.015em] ${ARTICLE_TOKENS.ink} ${SCROLL_MARGIN_CLASS} [text-wrap:pretty] [&+p]:mt-6`}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        className={`mt-9 mb-3 font-heading text-xl font-bold leading-[1.25] ${ARTICLE_TOKENS.ink}`}
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        className={`mt-7 mb-2 font-heading text-lg font-bold ${ARTICLE_TOKENS.ink}`}
      >
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote
        className={`my-6 border-l-[3px] border-[#3fa9f5] pl-5 font-body text-lg italic ${ARTICLE_TOKENS.bodyMuted}`}
      >
        {children}
      </blockquote>
    ),
    // The legacy `.article blockquote` rule draws a left border and indent, so
    // the important modifiers clear them for the centred style.
    pullquote: ({ children }) => (
      <blockquote
        className={`mx-auto my-10 max-w-[34rem] !border-x-0 !border-y-2 !border-[#1f7fc4] !px-0 py-6 text-center font-heading text-2xl font-bold not-italic leading-snug sm:text-[28px] ${ARTICLE_TOKENS.ink} [text-wrap:balance]`}
        data-cy="pull-quote"
      >
        {children}
      </blockquote>
    )
  },
  list: {
    bullet: ({ children }) => (
      <ul
        className={`mb-5 list-disc pl-6 font-body text-lg leading-[1.65] ${ARTICLE_TOKENS.body}`}
      >
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol
        className={`mb-5 list-decimal pl-6 font-body text-lg leading-[1.65] ${ARTICLE_TOKENS.body}`}
      >
        {children}
      </ol>
    )
  },
  types: {
    playerFixturesBlock: ({ value }: { value: SanityPlayerFixturesBlock }) => (
      <PlayerFixturesBlock value={value} data={blockData} />
    ),
    playerProfileBlock: ({ value }: { value: SanityPlayerProfileBlock }) => (
      <PlayerProfileBlock value={value} data={blockData} />
    ),
    headToHeadBlock: ({ value }: { value: SanityHeadToHeadBlock }) => (
      <HeadToHeadBlock value={value} data={blockData} />
    ),
    fixtureTickerBlock: ({ value }: { value: SanityFixtureTickerBlock }) => (
      <FixtureTickerBlock value={value} data={blockData} />
    ),
    statCalloutBlock: ({ value }: { value: SanityStatCalloutBlock }) => (
      <StatCalloutBlock value={value} />
    ),
    playerListBlock: ({ value }: { value: SanityPlayerListBlock }) => (
      <PlayerListBlock value={value} data={blockData} />
    ),
    ctaBannerBlock: ({ value }: { value: SanityCtaBannerBlock }) => (
      <CtaBannerBlock value={value} />
    ),
    embedBlock: ({ value }: { value: SanityEmbedBlock }) => (
      <EmbedBlock value={value} />
    ),
    image: ({ value }: { value: SanityBodyImage }) => (
      <BodyImage value={value} />
    )
  },
  marks: {
    strong: ({ children }) => (
      <strong className={`font-semibold ${ARTICLE_TOKENS.ink}`}>
        {children}
      </strong>
    ),
    link: ({ value, children }) => {
      const isExternal = Boolean(value?.blank);
      return withEdgeSpacesOutside(children, (linkText) => (
        <a
          href={value?.href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className={INLINE_LINK_CLASS_NAME}
        >
          {linkText}
        </a>
      ));
    },
    // The canonical path is resolved at build time from the player's
    // optaUuid. When that fails (a transfer, or the API being down) the link
    // goes through the by-id route, which resolves it on click and lands on
    // the players index for anyone it no longer knows.
    playerLink: ({
      value,
      children
    }: {
      value?: SanityPlayerLinkMark;
      children?: React.ReactNode;
    }) => {
      if (!value?.optaUuid) return <>{children}</>;
      const href =
        blockData.profilePathsByOptaUuid[value.optaUuid] ??
        toPlayerByIdPath(value.optaUuid);
      return withEdgeSpacesOutside(children, (linkText) => (
        <Link
          href={href}
          className={INLINE_LINK_CLASS_NAME}
          data-cy="player-link"
        >
          {linkText}
        </Link>
      ));
    }
  }
});
